import React, { useState, useEffect } from "react";
import { Shield, Users, Crown, Coins, ArrowDownToLine, Download, Flag, ScrollText, BarChart3, Loader2, Check, X, Ban, Pause } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";

const TABS = [["overview", "Overview", BarChart3], ["users", "Users", Users], ["plans", "Plans", Crown], ["rewards", "Earnings", Coins], ["payouts", "Payouts", ArrowDownToLine], ["downloads", "Downloads", Download], ["moderation", "Reports", Flag], ["audit", "Audit", ScrollText]];

function Field({ label, value, onChange, type = "number", step = "0.01" }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-400">{label}</span>
      <input type={type} step={step} value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
        className="w-full mt-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
    </label>
  );
}
function Toggle({ label, checked, onChange }) {
  return <label className="flex items-center justify-between py-2"><span className="text-sm text-slate-300">{label}</span>
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="w-5 h-5 accent-purple-500" /></label>;
}

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [uq, setUq] = useState("");
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [reports, setReports] = useState([]);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => setSettings(r.data.settings)).catch(() => {});
    api.get("/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
  }, []);
  useEffect(() => {
    if (tab === "users") api.get("/admin/users", { params: { q: uq } }).then((r) => setUsers(r.data.users)).catch(() => {});
    if (tab === "payouts") api.get("/admin/withdrawals").then((r) => setWithdrawals(r.data.withdrawals)).catch(() => {});
    if (tab === "moderation") api.get("/admin/reports").then((r) => setReports(r.data.reports)).catch(() => {});
    if (tab === "audit") api.get("/admin/audit-logs").then((r) => setLogs(r.data.logs)).catch(() => {});
  }, [tab, uq]);

  const saveSettings = async (section) => {
    try { await api.put("/admin/settings", { [section]: settings[section] }); toast.success("Saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setS = (section, key, val) => setSettings({ ...settings, [section]: { ...settings[section], [key]: val } });

  const savePlan = async (p) => { try { await api.put(`/admin/plans/${p.key}`, p); toast.success(`${p.name} saved`); } catch (e) { toast.error(errMsg(e)); } };
  const wdAction = async (id, action) => {
    let notes = "", reference = "";
    if (action === "reject") notes = window.prompt("Rejection note (optional)") || "";
    if (action === "complete") reference = window.prompt("Payout reference") || "manual";
    try { await api.put(`/admin/withdrawals/${id}`, { action, notes, reference }); api.get("/admin/withdrawals").then((r) => setWithdrawals(r.data.withdrawals)); toast.success(`Marked ${action}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const suspend = async (u) => { await api.put(`/admin/users/${u.id}/suspend`, null, { params: { suspend: !u.suspended } }); api.get("/admin/users", { params: { q: uq } }).then((r) => setUsers(r.data.users)); };
  const adjust = async (u) => {
    const amt = window.prompt(`Adjust ${u.display_name}'s wallet (+/- $)`); if (amt === null) return;
    const reason = window.prompt("Reason") || "admin adjustment";
    try { await api.post(`/admin/users/${u.id}/adjust`, { amount: Number(amt), reason }); api.get("/admin/users", { params: { q: uq } }).then((r) => setUsers(r.data.users)); toast.success("Adjusted"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6">
      <h1 className="text-2xl font-bold font-display flex items-center gap-2 mb-4"><Shield className="text-amber-400" /> Admin Dashboard</h1>
      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar border-b border-white/10">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${tab === k ? "border-amber-400 text-white" : "border-transparent text-slate-400"}`}><Icon size={15} /> {l}</button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {[["Users", stats.users], ["Songs", stats.songs], ["Active Subs", stats.active_subscriptions], ["Revenue", `$${Number(stats.revenue).toFixed(2)}`],
            ["Pending Payouts", stats.pending_withdrawals], ["Wallet Liability", `$${Number(stats.liabilities?.available || 0).toFixed(2)}`],
            ["Reward Budget", `$${Number(stats.reward_budget_total).toFixed(0)}`], ["Budget Spent", `$${Number(stats.reward_budget_spent).toFixed(2)}`],
            ["Lyric Videos", stats.videos], ["Render Failures", stats.video_failures], ["Downloads", stats.downloads]].map(([l, v]) => (
            <div key={l} className="jx-card rounded-2xl p-4"><p className="text-2xl font-black">{v}</p><p className="text-xs text-slate-500 mt-1">{l}</p></div>
          ))}
        </div>
      )}

      {tab === "users" && (
        <div>
          <input value={uq} onChange={(e) => setUq(e.target.value)} placeholder="Search users…" className="w-full max-w-sm bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm mb-4" />
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} className="jx-card rounded-xl p-3 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{u.display_name} <span className="text-xs text-slate-500">@{u.username}</span> {u.suspended && <span className="text-[10px] text-rose-400">SUSPENDED</span>}</p>
                  <p className="text-xs text-slate-500 truncate">{u.email} • {u.plan_key} • ${Number(u.wallet?.available_balance || 0).toFixed(2)} wallet</p>
                </div>
                <button onClick={() => adjust(u)} className="text-xs px-2.5 py-1.5 rounded-lg bg-white/5 text-slate-300">Adjust</button>
                <button onClick={() => suspend(u)} className={`text-xs px-2.5 py-1.5 rounded-lg ${u.suspended ? "bg-green-500/15 text-green-400" : "bg-rose-500/15 text-rose-400"}`}>{u.suspended ? "Reactivate" : "Suspend"}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "plans" && (
        <div className="space-y-4">
          {settings && (
            <div className="jx-card rounded-2xl p-5 border-purple-500/30">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold font-display">💳 Credits & AI Pricing</h3>
                <button onClick={() => saveSettings("credits")} data-testid="save-credits-btn" className="jx-cta text-white text-xs px-4 py-1.5 rounded-lg">Save Pricing</button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Markup multiplier (×)" value={settings.credits?.markup_multiplier} onChange={(v) => setS("credits", "markup_multiplier", v)} />
                <Field label="Credit value ($)" value={settings.credits?.credit_value_usd} onChange={(v) => setS("credits", "credit_value_usd", v)} />
                <Field label="ElevenLabs $/min (cost)" value={settings.credits?.engine_rates?.elevenlabs_music?.per_minute}
                  onChange={(v) => setS("credits", "engine_rates", { ...settings.credits.engine_rates, elevenlabs_music: { per_minute: v } })} />
                <Field label="Voice $/song (cost)" value={settings.credits?.engine_rates?.minimax_voice?.per_generation}
                  onChange={(v) => setS("credits", "engine_rates", { ...settings.credits.engine_rates, minimax_voice: { per_generation: v } })} />
              </div>
              <div className="mt-2"><Toggle label="Credit-based pricing enabled" checked={settings.credits?.enabled} onChange={(v) => setS("credits", "enabled", v)} /></div>
              <p className="text-xs text-slate-500 mt-1">Members are charged credits = ceil(engine_cost × markup ÷ credit_value). At 1.7× markup a 30s song (your cost $0.30) charges the member ~51 credits (≈$0.51).</p>
            </div>
          )}
          {plans.map((p, idx) => (
            <div key={p.key} className="jx-card rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3"><h3 className="font-bold font-display">{p.name} <span className="text-xs text-slate-500 font-mono">({p.key})</span></h3>
                <button onClick={() => savePlan(plans[idx])} className="jx-cta text-white text-xs px-4 py-1.5 rounded-lg">Save</button></div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Price / month" value={p.price_monthly} onChange={(v) => setPlans(plans.map((x, i) => i === idx ? { ...x, price_monthly: v } : x))} />
                <Field label="Price / year" value={p.price_annual} onChange={(v) => setPlans(plans.map((x, i) => i === idx ? { ...x, price_annual: v } : x))} />
                <Field label="Monthly credits" step="1" value={p.monthly_credits} onChange={(v) => setPlans(plans.map((x, i) => i === idx ? { ...x, monthly_credits: v } : x))} />
                <Field label="Max song seconds" step="1" value={p.max_song_seconds} onChange={(v) => setPlans(plans.map((x, i) => i === idx ? { ...x, max_song_seconds: v } : x))} />
                <Field label="Lyric exports / mo" step="1" value={p.lyric_export_limit} onChange={(v) => setPlans(plans.map((x, i) => i === idx ? { ...x, lyric_export_limit: v } : x))} />
              </div>
              <div className="flex gap-6 mt-3">
                <Toggle label="Can generate AI music" checked={p.can_generate} onChange={(v) => setPlans(plans.map((x, i) => i === idx ? { ...x, can_generate: v } : x))} />
                <Toggle label="Voice Studio" checked={p.can_voice} onChange={(v) => setPlans(plans.map((x, i) => i === idx ? { ...x, can_voice: v } : x))} />
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "rewards" && settings && (
        <div className="jx-card rounded-2xl p-5 max-w-2xl">
          <h3 className="font-bold font-display mb-3">Earnings & Reward Rules</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Listening reward / session ($)" value={settings.rewards?.listen_reward_per_session} onChange={(v) => setS("rewards", "listen_reward_per_session", v)} />
            <Field label="Creator reward / eligible play ($)" value={settings.rewards?.creator_reward_per_eligible_play} onChange={(v) => setS("rewards", "creator_reward_per_eligible_play", v)} />
            <Field label="Min listen seconds" step="1" value={settings.rewards?.min_listen_seconds} onChange={(v) => setS("rewards", "min_listen_seconds", v)} />
            <Field label="Daily earn limit ($)" value={settings.rewards?.daily_earn_limit} onChange={(v) => setS("rewards", "daily_earn_limit", v)} />
            <Field label="Monthly earn limit ($)" value={settings.rewards?.monthly_earn_limit} onChange={(v) => setS("rewards", "monthly_earn_limit", v)} />
            <Field label="Reward budget total ($)" value={settings.rewards?.reward_budget_total} onChange={(v) => setS("rewards", "reward_budget_total", v)} />
            <Field label="Earnings goal ($)" value={settings.rewards?.earnings_goal} onChange={(v) => setS("rewards", "earnings_goal", v)} />
            <Field label="Milestone label" type="text" value={settings.rewards?.milestone_label} onChange={(v) => setS("rewards", "milestone_label", v)} />
          </div>
          <div className="mt-3 grid sm:grid-cols-3">
            <Toggle label="Listening rewards" checked={settings.rewards?.listening_enabled} onChange={(v) => setS("rewards", "listening_enabled", v)} />
            <Toggle label="Creator rewards" checked={settings.rewards?.creator_enabled} onChange={(v) => setS("rewards", "creator_enabled", v)} />
            <Toggle label="⏸ Pause all rewards" checked={settings.rewards?.rewards_paused} onChange={(v) => setS("rewards", "rewards_paused", v)} />
          </div>
          <p className="text-xs text-slate-500 mt-2">Budget spent: ${Number(settings.rewards?.reward_budget_spent || 0).toFixed(2)}</p>
          <button onClick={() => saveSettings("rewards")} data-testid="save-rewards-btn" className="jx-cta text-white text-sm px-5 py-2 rounded-xl mt-4">Save Earnings Rules</button>

          <h3 className="font-bold font-display mt-6 mb-3">Payout Settings</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Minimum withdrawal ($)" value={settings.payouts?.min_withdrawal} onChange={(v) => setS("payouts", "min_withdrawal", v)} />
            <Field label="Withdrawal fee ($)" value={settings.payouts?.withdrawal_fee} onChange={(v) => setS("payouts", "withdrawal_fee", v)} />
          </div>
          <button onClick={() => saveSettings("payouts")} className="jx-cta text-white text-sm px-5 py-2 rounded-xl mt-4">Save Payout Settings</button>
        </div>
      )}

      {tab === "payouts" && (
        <div className="space-y-2">
          {withdrawals.length === 0 && <p className="text-slate-500 text-sm">No withdrawal requests.</p>}
          {withdrawals.map((w) => (
            <div key={w.id} className="jx-card rounded-xl p-3 flex items-center gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">${w.amount.toFixed(2)} → net ${w.net.toFixed(2)} <span className="text-xs text-slate-500">(fee ${w.fee.toFixed(2)})</span></p>
                <p className="text-xs text-slate-500 truncate">{w.user_name} • {w.user_email} • {w.method} • <span className="capitalize">{w.status}</span></p>
              </div>
              {["pending", "approved", "processing"].includes(w.status) && (
                <div className="flex gap-1.5">
                  {w.status === "pending" && <button onClick={() => wdAction(w.id, "approve")} className="text-xs px-2.5 py-1.5 rounded-lg bg-blue-500/15 text-blue-400">Approve</button>}
                  <button onClick={() => wdAction(w.id, "processing")} className="text-xs px-2.5 py-1.5 rounded-lg bg-cyan-500/15 text-cyan-400">Processing</button>
                  <button onClick={() => wdAction(w.id, "complete")} className="text-xs px-2.5 py-1.5 rounded-lg bg-green-500/15 text-green-400">Complete</button>
                  <button onClick={() => wdAction(w.id, "reject")} className="text-xs px-2.5 py-1.5 rounded-lg bg-rose-500/15 text-rose-400">Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "downloads" && settings && (
        <div className="jx-card rounded-2xl p-5 max-w-2xl">
          <h3 className="font-bold font-display mb-3">Downloads & Video Exports</h3>
          <div className="grid sm:grid-cols-2">
            <Toggle label="MP3 downloads enabled" checked={settings.downloads?.mp3_enabled} onChange={(v) => setS("downloads", "mp3_enabled", v)} />
            <Toggle label="WAV downloads enabled" checked={settings.downloads?.wav_enabled} onChange={(v) => setS("downloads", "wav_enabled", v)} />
            <Toggle label="Lyric video exports enabled" checked={settings.downloads?.lyric_video_enabled} onChange={(v) => setS("downloads", "lyric_video_enabled", v)} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <Field label="Max file size (MB)" step="1" value={settings.downloads?.max_file_mb} onChange={(v) => setS("downloads", "max_file_mb", v)} />
            <Field label="Max concurrent renders" step="1" value={settings.downloads?.max_concurrent_renders} onChange={(v) => setS("downloads", "max_concurrent_renders", v)} />
            <Field label="Max resolution" type="text" step="" value={settings.downloads?.max_resolution} onChange={(v) => setS("downloads", "max_resolution", v)} />
            <Field label="Export retention (days)" step="1" value={settings.downloads?.export_retention_days} onChange={(v) => setS("downloads", "export_retention_days", v)} />
          </div>
          <p className="text-xs text-slate-400 mt-3 mb-1">Monthly lyric-video export limits per plan:</p>
          <div className="grid grid-cols-4 gap-2">
            {["free", "creator", "pro", "studio"].map((k) => (
              <Field key={k} label={k} step="1" value={settings.downloads?.export_limits?.[k]}
                onChange={(v) => setS("downloads", "export_limits", { ...settings.downloads.export_limits, [k]: v })} />
            ))}
          </div>
          <button onClick={() => saveSettings("downloads")} data-testid="save-downloads-btn" className="jx-cta text-white text-sm px-5 py-2 rounded-xl mt-4">Save Download Settings</button>
        </div>
      )}

      {tab === "moderation" && (
        <div className="space-y-2">
          {reports.length === 0 && <p className="text-slate-500 text-sm">No reports.</p>}
          {reports.map((r) => (
            <div key={r.id} className="jx-card rounded-xl p-3">
              <p className="text-sm font-medium capitalize">{r.target_type} report — {r.reason}</p>
              <p className="text-xs text-slate-500">{r.details} • {new Date(r.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "audit" && (
        <div className="space-y-1.5">
          {logs.map((l) => (
            <div key={l.id} className="flex items-center justify-between text-sm py-2 border-b border-white/5">
              <span className="capitalize">{l.action.replace(/_/g, " ")} <span className="text-xs text-slate-500">{l.record}</span></span>
              <span className="text-xs text-slate-500">{new Date(l.timestamp).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
